import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { FaRegCopy } from "react-icons/fa6";
import NewTable from '../../components/NewTable/NewTable';
import Pagination from '../../components/Pagination/Pagination';
import Loader from '../../components/Atoms/Loader';
import { alertActions, coinManagementActions } from '../../_actions';

const PAGE_SIZE = 10;
const TRANSACTION_TYPE = "WALLET_TO_WALLET";

const Wallet2Wallet = () => {

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('ALL');
  const [pageNo, setPageNo] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [summaryData, setSummaryData] = useState({
    sent: { amount: '0.00', fees: '0.00' },
    received: { amount: '0.00', fees: '0.00' }
  });
  
  const dispatch = useDispatch();
  const { getCoin } = useSelector(state => state);
  const { loading, getHistoryList } = getCoin || {};
  const historyData = getHistoryList?.data?.list || [];
  const totalItems = getHistoryList?.data?.total || 0;
  
  // Fetch summary data
  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        const res = await coinManagementActions.getAmountAndCount({ 
          type: TRANSACTION_TYPE 
        });
        
        if (res?.data) {
          setSummaryData({
            sent: { 
              amount: (res.data.sentAmount ?? 0).toFixed(4), 
              fees: '100,990.000' 
            },
            received: { 
              amount: (res.data.receiveAmount ?? 0).toFixed(4), 
              fees: '100,990.000' 
            }
          });
        }
      } catch (err) {
        console.error("Error fetching summary data:", err);
      }
    };
    
    fetchSummaryData();
  }, []);

  
  useEffect(() => {
    const fetchData = () => {
      const data = {
        keyWord: searchTerm,
        fromDate,
        toDate,
        sortBy: "createdAt",
        sortOrder: "desc",
        pageNo,
        size: PAGE_SIZE,
        type: TRANSACTION_TYPE,
        status: sortBy === "all" ? "ALL" : sortBy
      };
      
      dispatch(coinManagementActions.getTransactionHistory(data));
    };
    
    fetchData();
  }, [pageNo, sortBy, fromDate, toDate, searchTerm, dispatch]);

  // Handlers
  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
    dispatch(alertActions.success("Address copied to clipboard."));
  }, [dispatch]);

  const handleSortByStatus = useCallback((e) => {
    setSortBy(e.target.value.toLowerCase());
    setPageNo(1); // Reset to first page when changing filters
  }, []);

  const onPageChange = useCallback((page) => {
    setPageNo(page);
  }, []);

  const handleDateRangeChange = useCallback(() => {
    setPageNo(1); // Reset to first page when changing date range
  }, []);

  // Constants
  const sortingOptions = [
    { value: 'ALL', label: 'Sort by All' },
    { value: 'pending', label: 'Sort by Pending' },
    { value: 'completed', label: 'Sort by Completed' },
    { value: 'failed', label: 'Sort by Failed' },
    { value: 'canceled', label: 'Sort by Canceled' },
  ];

  const statusStyles = {
    pending: 'bg-[#FFA600]',
    completed: 'bg-[#00811D]',
    failed: 'bg-[#FF4C4C]',
    canceled: 'bg-gray-500'
  };

  const tableHeadings = [
    'User', 'From', 'To', 'Hash', 'Credit', 'Debit', 'Mode', 'Status', 'Date'
  ];


  const tableRows = useMemo(() => 
    historyData?.map((ele, index) => {
      const truncatedFrom = ele?.from?.slice(0, 15) + (ele?.from?.length > 15 ? "..." : "");
      const truncatedTo = ele?.to?.slice(0, 15) + (ele?.to?.length > 15 ? "..." : "");
      const truncatedHash = ele?.hash?.slice(0, 15) + (ele?.hash?.length > 15 ? "..." : "");
      const fullHashLink = `https://pkrdscan.com/tx/${ele?.hash}`;
      return [
        <div key={`user-${index}`}>
          <p className="font-medium">{ele?.userId?.name || 'N/A'}</p>
          <p className="text-sm text-gray-600">{ele?.userId?.email || 'N/A'}</p>
        </div>,
        
        <div className="relative flex items-center group" key={`from-${index}`}>
          <span className="truncate" title={ele?.from}>{truncatedFrom || 'N/A'}</span>
          <div className="absolute left-0 px-2 py-1 mb-2 text-xs text-white transition-opacity duration-200 bg-black rounded opacity-0 bottom-full group-hover:opacity-100">
            {ele?.from}
          </div>
          {ele?.from && (
            <span
              className="ml-2 cursor-pointer hover:text-blue-500"
              onClick={() => handleCopy(ele.from)}
              title="Copy from address"
            >
              <FaRegCopy />
            </span>
          )}
        </div>,
        
        <div className="relative flex items-center group" key={`to-${index}`}>
          <span className="truncate" title={ele?.to}>{truncatedTo || 'N/A'}</span>
          <div className="absolute left-0 px-2 py-1 mb-2 text-xs text-white transition-opacity duration-200 bg-black rounded opacity-0 bottom-full group-hover:opacity-100">
            {ele?.to}
          </div>
          {ele?.to && (
            <span
              className="ml-2 cursor-pointer hover:text-blue-500"
              onClick={() => handleCopy(ele.to)}
              title="Copy to address"
            >
              <FaRegCopy />
            </span>
          )}
        </div>,
        
        <div key={`hash-${index}`}>
          {ele?.hash ? (
            <Link
              to={fullHashLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline flex items-center"
            >
              {truncatedHash}
            </Link>
          ) : (
            <span>N/A</span>
          )}
        </div>,
        
        ele?.transactionType === "RECEIVE" ? (ele?.amount ?? 0).toFixed(4) : "N/A",
        ele?.transactionType === "SEND" ? (Number(ele?.amount || 0).toFixed(4)) : "N/A",
        ele?.transactionType || "N/A",
        
        <div className='flex capitalize items-center' key={`status-${index}`}>
          <div className={`w-1 h-4 mr-2 rounded-md ${statusStyles[ele?.status] || 'bg-gray-500'}`}></div>
          {ele?.status || "N/A"}
        </div>,
        
        <span key={`date-${index}`}>
          {ele?.createdAt ? moment(ele.createdAt).format("MMMM Do YYYY, h:mm:ss a") : "N/A"}
        </span>
      ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }) || [], [historyData, handleCopy]);

  // JSX
  return (
    <div className='p-4 overflow-hidden overflow-y-auto'>
      <Loader loading={loading} />
      
      <NewTable
        Heading="Wallet to Wallet History"
        walletId="10009"
        tableHeadings={tableHeadings}
        data={tableRows}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showSearch={true}
        placeholder="Search by name, email, phone or ID..."
        summaryDataFull={summaryData}
        showSorting={true}
        onSortByStatus={handleSortByStatus}
        sortStatusValue={sortBy}
        sortingOptions={sortingOptions}
        fromDate={fromDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        toDate={toDate}
        showDateFilter={true}
        showPageTopData3={false}
        showPageTopData4={false}
        showDropDown1={true}
        handleDateRangeChange={handleDateRangeChange}
      />

      {Math.ceil(totalItems / PAGE_SIZE) > 1 && (
        <Pagination
          totalPages={Math.ceil(totalItems / PAGE_SIZE)}
          currentPage={pageNo}
          onPageChange={onPageChange}
          total={totalItems}
        />
      )}
    </div>
  );
};

export default Wallet2Wallet;